import { useMemo, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import useCategories from "../hooks/useCategories";
import useCourseFilterOptions from "../hooks/useCourseFilterOptions";

const categoryName = (item, language) => item?.name?.[language] || item?.name?.en || item?.name?.ps || item?.name?.pr || "";
const departmentName = (item, language) => item?.[language] || item?.en || item?.ps || item?.pr || "";

function SearchableMultiSelect({ label, options, selected, onChange, disabled = false, emptyText }) {
  const [term, setTerm] = useState("");
  const selectedSet = new Set(selected);
  const visibleOptions = useMemo(() => options.filter((option) => option.label.toLocaleLowerCase().includes(term.toLocaleLowerCase())), [options, term]);
  const toggle = (value) => onChange(selectedSet.has(value) ? selected.filter((item) => item !== value) : [...selected, value]);

  return <fieldset disabled={disabled} className="min-w-0">
    <legend className="mb-1 text-sm font-medium text-blue-gray-800">{label}</legend>
    <div className="rounded-lg border border-blue-gray-100 bg-white p-2 focus-within:ring-2 focus-within:ring-indigo-500">
      <div className="relative">
        <FaSearch aria-hidden="true" className="absolute start-3 top-3 text-gray-400" size={13} />
        <input type="search" value={term} onChange={(event) => setTerm(event.target.value)} placeholder={label}
          className="w-full rounded-md border border-gray-200 py-2 ps-8 pe-3 text-sm outline-none disabled:bg-gray-50" />
      </div>
      {selected.length > 0 && <div className="mt-2 flex flex-wrap gap-1">
        {selected.map((value) => {
          const option = options.find((item) => item.value === value);
          return option && <button type="button" key={value} onClick={() => toggle(value)} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-1 text-xs text-indigo-800 focus:ring-2 focus:ring-indigo-500">
            {option.label} <FaTimes aria-hidden="true" />
          </button>;
        })}
      </div>}
      <div className="mt-2 max-h-40 overflow-y-auto" role="listbox" aria-label={label} aria-multiselectable="true">
        {visibleOptions.length ? visibleOptions.map((option) => <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50">
          <input type="checkbox" checked={selectedSet.has(option.value)} onChange={() => toggle(option.value)} />
          <span>{option.label}</span>
        </label>) : <p className="px-2 py-2 text-sm text-gray-500">{emptyText}</p>}
      </div>
    </div>
  </fieldset>;
}

export default function CourseSearchFilters({ params, updateFilters, reset, showSorting = true }) {
  const { t, i18n } = useTranslation();
  const { data: categories, isLoading } = useCategories();
  const { teachers, universities, isLoading: universitiesLoading } = useCourseFilterOptions();
  const selectedTeachers = (params.get("instructor") || "").split(",").filter(Boolean);
  const selectedUniversities = (params.get("university") || "").split(",").filter(Boolean);
  const selectedFaculties = (params.get("category") || "").split(",").filter(Boolean);
  const selectedDepartments = (params.get("subCategoryId") || "").split(",").filter(Boolean);
  const facultyOptions = categories.map((category) => ({ value: category._id, label: categoryName(category, i18n.language) })).filter((item) => item.label);
  const departmentOptions = categories.filter((category) => selectedFaculties.includes(category._id)).flatMap((category) =>
    (category.subCategories || []).map((department) => ({ value: department._id, label: departmentName(department, i18n.language) })),
  ).filter((item) => item.label);
  const universityOptions = universities.map((university) => ({ value: university._id, label: categoryName(university, i18n.language) })).filter((item) => item.label);
  const teacherOptions = teachers.map((teacher) => ({ value: teacher._id, label: teacher.fullName || "" })).filter((item) => item.label);
  const label = (key) => t(`courseSearch.${key}`);

  return <div className="flex flex-col gap-4">
    <SearchableMultiSelect label={label("university")} options={universityOptions} selected={selectedUniversities}
      emptyText={universitiesLoading ? t("courseSearch.loading", "Loading options…") : t("courseSearch.noOptions", "No matching options")}
      onChange={(values) => updateFilters({ university: values.join(",") })} />
    <SearchableMultiSelect label={label("faculty")} options={facultyOptions} selected={selectedFaculties}
      emptyText={isLoading ? t("courseSearch.loading", "Loading options…") : t("courseSearch.noOptions", "No matching options")}
      onChange={(values) => updateFilters({ category: values.join(","), subCategoryId: "" })} />
    <SearchableMultiSelect label={label("subCategoryId")} options={departmentOptions} selected={selectedDepartments}
      disabled={!selectedFaculties.length} emptyText={selectedFaculties.length ? t("courseSearch.noOptions", "No matching options") : t("courseSearch.selectFacultyFirst", "Select a faculty first")}
      onChange={(values) => updateFilters({ subCategoryId: values.join(",") })} />
    <SearchableMultiSelect label={label("instructor")} options={teacherOptions} selected={selectedTeachers}
      emptyText={universitiesLoading ? label("loading") : label("noOptions")}
      onChange={(values) => updateFilters({ instructor: values.join(",") })} />
    <label className="block text-sm">{label("language")}
      <select value={params.get("language") || ""} onChange={(event) => updateFilters({ language: event.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-indigo-500">
        <option value="">{label("all")}</option>
        {["ps", "pr", "en"].map((value) => <option value={value} key={value}>{label(value)}</option>)}
      </select>
    </label>
    <label className="block text-sm">{label("minRating")}
      <select value={params.get("minRating") || ""} onChange={(event) => updateFilters({ minRating: event.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-indigo-500">
        <option value="">{label("all")}</option>
        {[1, 2, 3, 4, 5].map((value) => <option value={value} key={value}>{label(`rating${value}`)}</option>)}
      </select>
    </label>
    <label className="block text-sm">{label("credits")}
      <select value={params.get("credits") || ""} onChange={(event) => updateFilters({ credits: event.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-indigo-500">
        <option value="">{label("all")}</option>
        {[1, 2, 3, 4, 5].map((value) => <option value={value} key={value}>{value}</option>)}
      </select>
    </label>
    {showSorting && <label className="block text-sm">{label("sortField")}
      <select value={params.get("sortField") || ""} onChange={(event) => updateFilters({ sortField: event.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-indigo-500">
        <option value="">{label("createdAt")}</option>
        {["views", "averageRating", "title.en", "title.ps", "title.pr"].map((value) => <option value={value} key={value}>{label(value.replace(".", "_"))}</option>)}
      </select>
    </label>}
    {showSorting && <label className="block text-sm">{label("sortOrder")}
      <select value={params.get("sortOrder") || ""} onChange={(event) => updateFilters({ sortOrder: event.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-indigo-500">
        <option value="">{label("descending")}</option><option value="1">{label("ascending")}</option>
      </select>
    </label>}
    <button type="button" onClick={reset} className="rounded-lg border px-3 py-2 text-indigo-700 focus:ring-2 focus:ring-indigo-500">{label("reset")}</button>
  </div>;
}
